'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  Pagination,
  SearchInput,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDate,
} from '@rr/ui'
import { useCompanyContactPositions } from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 20

export default function ContactPositionsList({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({ page, limit: PAGE_SIZE, search: search.trim() || undefined }),
    [page, search],
  )

  const { data, isLoading, isError, error } = useCompanyContactPositions(companyId, filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  return (
    <EntityListShell
      toolbar={
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1) }}
          placeholder="Search by name or code…"
          className="max-w-sm"
        />
      }
      empty={!isLoading && items.length === 0}
      emptyState={
        isError
          ? error instanceof Error ? error.message : 'Failed to load contact positions.'
          : 'No contact positions visible to this organization.'
      }
      pagination={<Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium text-foreground">{p.name ?? '—'}</TableCell>
                <TableCell className="text-sm tnum">{p.code ?? '—'}</TableCell>
                <TableCell className="text-sm">{p.isActive ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  {p.isGlobal ? (
                    <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">Global</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Organization</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(p.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}
